import { cn } from '@/components/ui/utils';
import dayjs from 'dayjs';
import { Calendar, ChevronDown } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export interface DateRange {
  from?: Date;
  to?: Date;
}

export interface DateRangePickerProps {
  value?: DateRange;
  onChange: (range: DateRange | undefined) => void;
  isActive?: boolean;
  className?: string;
  minDate?: Date;
}

type DatePreset =
  | 'all'
  | 'today'
  | 'last7days'
  | 'last30days'
  | 'thisMonth'
  | 'lastMonth'
  | 'custom';

const datePresetLabels: Record<DatePreset, string> = {
  all: 'All time',
  today: 'Today',
  last7days: 'Last 7 days',
  last30days: 'Last 30 days',
  thisMonth: 'This month',
  lastMonth: 'Last month',
  custom: 'Custom range',
};

function getDateRangeForPreset(
  preset: DatePreset
): { from?: Date; to?: Date } | undefined {
  const now = dayjs();

  switch (preset) {
    case 'all':
      return undefined;
    case 'today':
      return {
        from: now.startOf('day').toDate(),
        to: now.endOf('day').toDate(),
      };
    case 'last7days':
      return {
        from: now.subtract(7, 'day').startOf('day').toDate(),
        to: now.endOf('day').toDate(),
      };
    case 'last30days':
      return {
        from: now.subtract(30, 'day').startOf('day').toDate(),
        to: now.endOf('day').toDate(),
      };
    case 'thisMonth':
      return {
        from: now.startOf('month').toDate(),
        to: now.endOf('month').toDate(),
      };
    case 'lastMonth':
      return {
        from: now.subtract(1, 'month').startOf('month').toDate(),
        to: now.subtract(1, 'month').endOf('month').toDate(),
      };
    case 'custom':
      return undefined;
    default:
      return undefined;
  }
}

function detectDatePreset(dateRange?: DateRange): DatePreset {
  if (!dateRange?.from && !dateRange?.to) return 'all';

  const now = dayjs();
  const from = dateRange.from ? dayjs(dateRange.from) : null;
  const to = dateRange.to ? dayjs(dateRange.to) : null;

  if (from && to) {
    if (from.isSame(now, 'day') && to.isSame(now, 'day')) {
      return 'today';
    }

    if (from.isSame(now.subtract(7, 'day'), 'day') && to.isSame(now, 'day')) {
      return 'last7days';
    }

    if (from.isSame(now.subtract(30, 'day'), 'day') && to.isSame(now, 'day')) {
      return 'last30days';
    }

    if (
      from.isSame(now.startOf('month'), 'day') &&
      to.isSame(now.endOf('month'), 'day')
    ) {
      return 'thisMonth';
    }

    const lastMonth = now.subtract(1, 'month');
    if (
      from.isSame(lastMonth.startOf('month'), 'day') &&
      to.isSame(lastMonth.endOf('month'), 'day')
    ) {
      return 'lastMonth';
    }
  }

  return 'custom';
}

export function DateRangePicker({
  value,
  onChange,
  isActive,
  className,
  minDate,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  const currentPreset = useMemo(() => detectDatePreset(value), [value]);

  const handlePresetChange = useCallback(
    (preset: DatePreset) => {
      if (preset === 'custom') {
        return;
      }
      const dateRange = getDateRangeForPreset(preset);
      onChange(dateRange);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleCustomDateSelect = useCallback(
    (date: Date | undefined, type: 'from' | 'to') => {
      const currentRange = value || {};
      const newRange = {
        ...currentRange,
        [type]:
          type === 'to' && date ? dayjs(date).endOf('day').toDate() : date,
      };
      onChange(newRange);
    },
    [value, onChange]
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className={cn(
            'h-9 gap-2',
            isActive && 'border-primary rounded-r-none border-r-0',
            className
          )}
        >
          <Calendar className='w-4 h-4' />
          {datePresetLabels[currentPreset]}
          <ChevronDown className='w-3 h-3' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-auto p-0' align='start'>
        <div className='p-2 space-y-1'>
          {(Object.keys(datePresetLabels) as DatePreset[])
            .filter((k) => k !== 'custom')
            .map((preset) => (
              <Button
                key={preset}
                variant={currentPreset === preset ? 'secondary' : 'ghost'}
                size='sm'
                className='w-full justify-start'
                onClick={() => handlePresetChange(preset)}
              >
                {datePresetLabels[preset]}
              </Button>
            ))}
          <div className='border-t my-2' />
          <div className='text-xs text-muted-foreground px-2 pb-1'>
            Custom range:
          </div>
          <div className='flex gap-2'>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant='outline' size='sm' className='flex-1 text-xs'>
                  {value?.from ? dayjs(value.from).format('MMM D') : 'From'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-0'>
                <CalendarComponent
                  mode='single'
                  selected={value?.from}
                  onSelect={(date) => handleCustomDateSelect(date, 'from')}
                  fromDate={minDate}
                />
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant='outline' size='sm' className='flex-1 text-xs'>
                  {value?.to ? dayjs(value.to).format('MMM D') : 'To'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-auto p-0'>
                <CalendarComponent
                  mode='single'
                  selected={value?.to}
                  onSelect={(date) => handleCustomDateSelect(date, 'to')}
                  fromDate={minDate}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
